/**
 * @fileoverview Pure helpers for the folder path convention.
 *
 * The library root is "" (documents with no folder). Folder paths always
 * start with "/" and never end with one: "/Bank Statements/2026".
 * `folderId` is the source of truth for location; paths are display
 * caches repaired in batches after renames/moves.
 */

/** `name` must be a sanitized segment (no path separators) — see sanitizeFolderName. */
export function joinFolderPath(parentPath: string, name: string): string {
  return `${parentPath}/${name}`;
}

/** True when `path` is `ancestorPath` or lives anywhere underneath it. */
export function isSameOrDescendantPath(
  path: string,
  ancestorPath: string,
): boolean {
  if (ancestorPath === "") return true;
  return path === ancestorPath || path.startsWith(`${ancestorPath}/`);
}

/** Rewrite `path` replacing the `oldAncestor` prefix with `newAncestor`. */
export function rebasePath(
  path: string,
  oldAncestor: string,
  newAncestor: string,
): string {
  if (!isSameOrDescendantPath(path, oldAncestor)) {
    throw new Error(`rebasePath: "${path}" is not under "${oldAncestor}"`);
  }
  return `${newAncestor}${path.slice(oldAncestor.length)}`;
}

/** Folder names cannot contain path separators; empty result = invalid. */
export function sanitizeFolderName(raw: string): string {
  return raw.replace(/[/\\]/g, "").trim();
}
